import { useId, useState } from 'react'
import type { ExploreDetail, Scene, SourceRef } from '../content'

const SOURCE_KIND_LABEL: Record<SourceRef['kind'], string> = {
  rfp: 'District requirement',
  qa: 'District clarification',
  proposal: 'Submitted proposal',
  architecture: 'Proposed architecture',
  cv: 'Submitted resume',
  company: 'Merlion IT profile',
  literature: 'Published literature',
}

export function SourceNote({ sources }: { sources: SourceRef[] }) {
  if (!sources.length) return null
  return (
    <p className="sources">
      {sources.map((s, i) => (
        <span key={`${s.label}-${i}`} className={`sources__item sources__item--${s.kind}`}>
          <span className="sources__kind">{SOURCE_KIND_LABEL[s.kind]}</span>
          <span className="sources__label">{s.label}</span>
          {s.locator ? <span className="sources__locator">{s.locator}</span> : null}
        </span>
      ))}
    </p>
  )
}

export function DetailTable({ table }: { table: NonNullable<ExploreDetail['table']> }) {
  return (
    <div className="table-wrap">
      <table>
        {table.head ? (
          <thead>
            <tr>
              {table.head.map((h) => (
                <th key={h} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ExploreDetails({ details }: { details: ExploreDetail[] }) {
  const id = useId()
  if (!details.length) return null
  return (
    <section className="explore" aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className="explore__heading">
        Explore Details
      </h3>
      <div className="explore__items">
        {details.map((detail, i) => (
          <details key={detail.title} className="explore__item" open={i === 0 && details.length === 1}>
            <summary className="explore__summary">
              <span>{detail.title}</span>
              <span className="explore__chev" aria-hidden="true">
                <svg viewBox="0 0 12 8" width="12" height="8" fill="none">
                  <path d="M1 1.5 6 6.5l5-5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </summary>
            <div className="explore__content">
              {detail.body?.map((p, j) => (
                <p key={j} className="explore__para">
                  {p}
                </p>
              ))}
              {detail.bullets?.length ? (
                <ul className="explore__bullets">
                  {detail.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
              ) : null}
              {detail.table ? <DetailTable table={detail.table} /> : null}
              {detail.sources?.length ? <SourceNote sources={detail.sources} /> : null}
            </div>
          </details>
        ))}
      </div>
    </section>
  )
}

export function RfpAlignmentDrawer({ items }: { items: string[] }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  if (!items.length) return null
  return (
    <div className="alignment">
      <button
        type="button"
        className="alignment__toggle"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="alignment__icon" aria-hidden="true">
          <svg viewBox="0 0 14 14" width="14" height="14" fill="none">
            <path d="M2 7.5 5.5 11 12 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        RFP Alignment
        <span className="alignment__count">{items.length}</span>
      </button>
      <ul id={id} className="alignment__list" hidden={!open}>
        {items.map((item) => (
          <li key={item}>
            <span className="alignment__check" aria-hidden="true">
              ✓
            </span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Callouts({ items }: { items: NonNullable<Scene['callouts']> }) {
  return (
    <ul className="callouts">
      {items.map((c) => (
        <li key={c.label} className="callouts__item">
          <span className="callouts__label">{c.label}</span>
          {c.detail ? <span className="callouts__detail">{c.detail}</span> : null}
        </li>
      ))}
    </ul>
  )
}

export function WhyItMatters({ text }: { text: string }) {
  return (
    <aside className="why">
      <span className="why__label">Why it matters</span>
      <p className="why__text">{text}</p>
    </aside>
  )
}
